import clsx from 'clsx';
import React from 'react';
import { BreadcrumbList, WithContext } from 'schema-dts';
import { Link } from 'gatsby';
import { SvgArrowLeft } from '../../../icons';
import { Helmet } from 'react-helmet';
import { Typography } from '../atoms/typography';
import translate from '../../../translations';
import { Language } from '../../../interfaces';

export type BreadcrumbLink = {
    title: string;
    url: string;
};

type Props = React.DetailedHTMLProps<React.HTMLAttributes<HTMLElement>, HTMLElement> & {
    links: BreadcrumbLink[];
    language: Language;
};

function Separator() {
    return (
        <Typography
            as="span"
            variant="button-sm"
            className="hidden px-2 text-neutral-600 md:inline"
            aria-hidden="true"
        >
            /
        </Typography>
    );
}

export function Breadcrumbs({ links, className, language, ...props }: Props) {
    const pageUrl = process.env.GATSBY_STOREFRONT_URL;

    const homeTitle = translate(language, 'home') || 'Home';

    const listItems = [
        {
            title: homeTitle,
            url: `/${language}`,
        },
        ...links,
    ];

    const schema: WithContext<BreadcrumbList> = {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: listItems.map((link, i) => {
            return {
                '@type': 'ListItem',
                position: i + 1,
                name: link.title,
                item: `${pageUrl}${link.url}`,
            };
        }),
    };

    const linkCount = links.length;

    const isHeroRoot = linkCount <= 1;

    return (
        <>
            <Helmet
                script={[
                    {
                        type: 'application/ld+json',
                        innerHTML: JSON.stringify(schema),
                    },
                ]}
            ></Helmet>

            <nav
                className={clsx('text-neutral-700', className)}
                aria-label={translate(language, 'breadcrumbs')}
                {...props}
            >
                <ul className="flex whitespace-nowrap">
                    <li
                        className={clsx({
                            'hidden md:inline-block': !isHeroRoot,
                        })}
                    >
                        <Link to={`/${language}`} className="hover:underline">
                            <Typography
                                variant="button-sm"
                                className="text-neutral-700 p-1 inline-flex items-center hover:bg-neutral-200 hover:text-neutral-900"
                                as={'span'}
                            >
                                {isHeroRoot ? (
                                    <SvgArrowLeft className="mr-1 inline h-4 w-4 md:hidden shrink-0 md:hidden" />
                                ) : null}
                                {homeTitle}
                            </Typography>
                        </Link>
                        <Separator />
                    </li>

                    {links.map((link, index) => {
                        const isLast = index === linkCount - 1;
                        // On mobile we need to second last link beecause last breadcrumb is not clickable
                        const isSecondLast = index === linkCount - 2;

                        return (
                            <li
                                key={index}
                                className={clsx({
                                    'hidden md:block': isHeroRoot || !isSecondLast,
                                })}
                            >
                                {isLast ? (
                                    <Typography
                                        variant="button-sm"
                                        className="text-neutral-900 p-1"
                                        as={'span'}
                                    >
                                        {link.title}
                                    </Typography>
                                ) : (
                                    <>
                                        <Link to={link.url} className="hover:underline">
                                            <Typography
                                                variant="button-sm"
                                                className="text-neutral-700 p-1 inline-flex items-center hover:bg-neutral-200 hover:text-neutral-900"
                                                as={'span'}
                                            >
                                                {isSecondLast ? (
                                                    <SvgArrowLeft className="mr-1 inline h-4 w-4 md:hidden shrink-0" />
                                                ) : null}
                                                {link.title}
                                            </Typography>
                                        </Link>
                                        <Separator />
                                    </>
                                )}
                            </li>
                        );
                    })}
                </ul>
            </nav>
        </>
    );
}
